<script setup lang="ts">
import { ActionButton, Dialog } from '@taler/ui'
import { formatCount, formatDate, type Locale } from '@taler/domain'
import { computed, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import CarPhoto from '@/features/garage/components/CarPhoto.vue'
import CarStatusBadge from '@/features/garage/components/CarStatusBadge.vue'
import { carSubtitle } from '../internal/car-presentation.ts'
import type { Car } from '../internal/types.ts'

const props = defineProps<{
  car: Car
  photoUrl: string | undefined
  canManage: boolean
  isDeleting: boolean
}>()
const emit = defineEmits<{ edit: []; delete: [] }>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as Locale)

const confirmingDelete = ref(false)
const deleteTrigger = useTemplateRef<HTMLButtonElement>('deleteTrigger')

const subtitle = computed(() => carSubtitle(props.car))

const details = computed(() => [
  { label: t('garage.details.licensePlate'), value: props.car.licensePlate ?? '—' },
  { label: t('garage.details.vin'), value: props.car.vin ?? '—', mono: true },
  { label: t('garage.details.fuelType'), value: t(`garage.fuelType.${props.car.fuelType}`) },
  { label: t('garage.details.color'), value: props.car.color ?? '—' },
  {
    label: t('garage.details.purchaseDate'),
    value:
      props.car.purchaseDate === undefined ? '—' : formatDate(props.car.purchaseDate, currentLocale.value),
  },
  {
    label: t('garage.details.initialMileage'),
    value: `${formatCount(props.car.initialMileageKm, currentLocale.value)} km`,
  },
  { label: t('garage.details.notes'), value: props.car.notes ?? '—' },
])

function confirmDelete(): void {
  confirmingDelete.value = false
  emit('delete')
}
</script>

<template>
  <header class="flex flex-col gap-4">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="flex min-w-0 items-start gap-3">
        <CarPhoto :src="photoUrl" alt="" size="wide" />
        <div class="flex min-w-0 flex-col gap-1">
          <div class="flex flex-wrap items-center gap-2">
            <h2 class="text-[length:var(--text-step-5)] font-semibold text-[color:var(--color-text)]">
              {{ car.name }}
            </h2>
            <span
              v-if="car.isPrimary"
              class="text-[length:var(--text-step-4)] text-[color:var(--color-accent)]"
              :title="t('garage.primary')"
            >
              <span aria-hidden="true">★</span>
              <span class="sr-only">{{ t('garage.primary') }}</span>
            </span>
            <CarStatusBadge :status="car.status" />
          </div>
          <p v-if="subtitle !== undefined" class="text-[color:var(--color-text-muted)]">{{ subtitle }}</p>
        </div>
      </div>
      <div v-if="canManage" class="flex items-center gap-2">
        <ActionButton variant="secondary" type="button" @click="emit('edit')">
          {{ t('garage.edit') }}
        </ActionButton>
        <button
          ref="deleteTrigger"
          type="button"
          class="min-h-[var(--density-target-min)] whitespace-nowrap rounded-[var(--radius-control)] border border-[color:var(--color-border)] px-3 py-2 text-[color:var(--color-danger)] hover:bg-[color:var(--color-surface-raised)]"
          @click="confirmingDelete = true"
        >
          {{ t('garage.delete') }}
        </button>
      </div>
    </div>

    <dl class="flex flex-wrap gap-x-6 gap-y-3">
      <div
        v-for="item in details"
        :key="item.label"
        class="flex min-w-0 flex-col gap-1 border-l border-[color:var(--color-border)] pl-4 first:border-l-0 first:pl-0"
      >
        <dt
          class="whitespace-nowrap text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
        >
          {{ item.label }}
        </dt>
        <dd
          :class="[
            'break-words text-[length:var(--text-step-3)] text-[color:var(--color-text)]',
            item.mono ? 'whitespace-nowrap font-mono' : undefined,
          ]"
        >
          {{ item.value }}
        </dd>
      </div>
    </dl>

    <Dialog
      :open="confirmingDelete"
      :label="t('garage.deleteCarTitle', { name: car.name })"
      @close="confirmingDelete = false"
      @close-auto-focus="deleteTrigger?.focus()"
    >
      <div class="flex flex-col gap-4 p-[var(--space-block)]">
        <h2
          class="font-[family-name:var(--font-display)] text-[length:var(--text-step-5)] font-semibold text-[color:var(--color-text)]"
        >
          {{ t('garage.deleteCarTitle', { name: car.name }) }}
        </h2>
        <p class="text-[color:var(--color-text-muted)]">
          {{ t('garage.deleteCarBody', { name: car.name }) }}
        </p>
        <div class="flex justify-end gap-2">
          <ActionButton variant="secondary" type="button" @click="confirmingDelete = false">
            {{ t('garage.cancel') }}
          </ActionButton>
          <button
            type="button"
            class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] bg-[color:var(--color-danger)] px-3 py-2 text-[color:var(--color-action-text)]"
            :disabled="isDeleting"
            @click="confirmDelete"
          >
            {{ t('garage.deleteCarConfirm') }}
          </button>
        </div>
      </div>
    </Dialog>
  </header>
</template>
